import { useId, useState, type ReactNode } from 'react'

import { BrainScan } from './brain'
import { useMediaQuery } from '../lib/useMediaQuery'
import { BREAKPOINTS } from '../lib/breakpoints'

/**
 * The frame every auth screen sits in.
 *
 * Two compositions, from `docs/design/auth/` canvas 01:
 *
 *   Desktop · Split   (>= 1024px) hero panel left, a 454px form column right.
 *   Mobile · Stacked  (<  1024px) one scrolling column, centred wordmark, a
 *                                 small brain under it, then the form.
 *
 * The canvas also shows Google, Apple, a magic link, "Keep me signed in" and
 * headband pairing. None are in scope: sign-in is Cognito SRP with an email and
 * a password. They are omitted rather than stubbed -- a disabled "Continue with
 * Google" is a promise the product has not made.
 */

/**
 * Two COMPLEMENTARY queries, not `wide` and `!wide`.
 *
 * `useMediaQuery` returns false when `matchMedia` is absent, so in jsdom both
 * are false and NO canvas mounts in any unit test -- `auth.test.tsx` needs no
 * edit and no lazy-chunk `act()` churn, which is what criterion 9 asks for. In
 * a browser exactly one is true.
 *
 * `not (min-width: …)` rather than `max-width: 1023.98px`, which would both
 * duplicate the breakpoint as a second magic number and put a subtraction in
 * this file -- `src/test/clientScience.test.ts` inventories every arithmetic
 * operator in `src/` and would want an allowlist entry for it.
 */
const DESKTOP = `(min-width: ${BREAKPOINTS.lg}px)`
const STACKED = `(not (min-width: ${BREAKPOINTS.lg}px))`

/**
 * The brain is DECORATION here, and both flags say so.
 *
 * `interactive={false}` is the one this sprint added: a popover opening behind
 * a sign-in form is a defect, and the hero is inside an `aria-hidden` panel
 * where nothing should be reachable anyway. The canvas's own desktop frame
 * passes neither flag; that difference is forced by criterion 7 and is written
 * down in `docs/1.9.3-fidelity.md`.
 */
const HERO_BRAIN = { size: 320, maxPoints: 2400, showLabels: false, interactive: false } as const
const SMALL_BRAIN = { size: 138, maxPoints: 1200, showLabels: false, interactive: false } as const

/** The settling EEG line at the foot of the hero, verbatim from the canvas. */
function Waveform() {
  return (
    <svg className="auth__wave" viewBox="0 0 420 34" preserveAspectRatio="none" aria-hidden="true">
      <path
        d="M0 17 H150 l7 -10 l5 20 l7 -26 l5 30 l7 -16 l6 8 H280 l8 -11 l5 13 H420"
        stroke="currentColor"
        strokeWidth="1.5"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export function AuthChrome({
  eyebrow,
  title,
  lead,
  children,
  footer,
}: {
  /**
   * The mono kicker above the title. REQUIRED, not optional: criterion 3 says
   * every signed-out screen reads eyebrow -> title -> lead, and a required prop
   * makes the compiler the gate for the sixth screen nobody has written yet.
   */
  eyebrow: string
  title: string
  lead?: ReactNode
  children: ReactNode
  footer?: ReactNode
}) {
  const wide = useMediaQuery(DESKTOP)
  const narrow = useMediaQuery(STACKED)

  return (
    <div className="auth">
      <aside className="auth__hero" aria-hidden="true">
        <div className="auth__hero-top">
          {/*
            `mark.svg`, not a wordmark. The wordmark that used to be here ships
            #F8FAFC ink, which is 1.13:1 on the light hero -- the word "Spot"
            was invisible. The mark is theme-neutral, so there is no light/dark
            pair to get wrong. It stays an `<img src>`: its `<linearGradient>`
            id is document-scoped to the asset and cannot collide, which stops
            being true the moment anyone inlines it.
          */}
          <img className="auth__hero-mark" src="/mark.svg" alt="" />
          <span className="pill pill--cyan pill--glow">
            <i className="pill__dot" />
            LIVE SIGNAL
          </span>
        </div>

        <div className="auth__figure">
          <div className="auth__halo" />
          {wide && <BrainScan {...HERO_BRAIN} />}
          <span className="pill">BRAINBIT PRO · 8 CH · 250 HZ</span>
        </div>

        <div className="auth__hero-copy">
          <p className="auth__hero-line">See what shapes your brain.</p>
          <p className="auth__hero-sub">
            Build a personal brain baseline, then watch how sleep, focus and recovery
            move it.
          </p>
          <Waveform />
        </div>
      </aside>

      <main className="auth__panel" id="main">
        <div className="auth__panel-inner">
          <div className="auth__brand">
            <img className="brand__mark brand__mark--dark" src="/wordmark-on-dark.svg" alt="BrainSpot" />
            <img className="brand__mark brand__mark--light" src="/wordmark-on-light.svg" alt="" aria-hidden="true" />
          </div>

          {/*
            `aria-hidden` HERE, not inherited: unlike the desktop hero this
            figure is not inside the aside, so without it a phone screen-reader
            user hears eight sensor names in the middle of a sign-in form.
          */}
          {narrow && (
            <div className="auth__figure auth__figure--sm" aria-hidden="true">
              <div className="auth__halo" />
              <BrainScan {...SMALL_BRAIN} />
            </div>
          )}

          <header className="auth__head">
            <div className="auth__eyebrow">{eyebrow}</div>
            <h1 className="auth__title">{title}</h1>
            {lead && <p className="auth__lead">{lead}</p>}
          </header>

          {children}

          {footer && <div className="auth__foot">{footer}</div>}

          <p className="auth__trust">Wellness and self-awareness tool. Not a medical device.</p>
        </div>
      </main>
    </div>
  )
}

/**
 * A password input with a reveal toggle.
 *
 * The toggle is `tabIndex={-1}` — AIVY's choice, kept: a keyboard user tabbing
 * from the password field wants the submit button, not a control that changes
 * nothing but the rendering. It stays reachable by pointer and by screen-reader
 * navigation, and `aria-pressed` says which state it is in.
 */
export function PasswordField({
  label,
  value,
  onChange,
  hint,
  error,
  autoComplete = 'current-password',
  autoFocus,
  required = true,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  hint?: ReactNode
  error?: string | null
  autoComplete?: string
  autoFocus?: boolean
  required?: boolean
}) {
  const [show, setShow] = useState(false)
  const id = useId()
  const hintId = `${id}-hint`
  const errorId = `${id}-error`
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ')

  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>{label}</label>
      <div className="field__reveal-wrap">
        <input
          id={id}
          className="field__control field__control--reveal"
          type={show ? 'text' : 'password'}
          value={value}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          required={required}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy || undefined}
          onChange={(e) => onChange(e.target.value)}
        />
        <button
          type="button"
          className="field__reveal"
          tabIndex={-1}
          aria-pressed={show}
          onClick={() => setShow((s) => !s)}
        >
          {show ? <EyeOffIcon /> : <EyeIcon />}
          <span className="visually-hidden">{show ? 'Hide password' : 'Show password'}</span>
        </button>
      </div>
      {hint && <span className="field__hint" id={hintId}>{hint}</span>}
      {error && <span className="field__error" id={errorId}>{error}</span>}
    </div>
  )
}

/* Lucide `eye` / `eye-off`, inlined — see AppShell for why not lucide-react. */
function EyeIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  )
}

function EyeOffIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M10.7 6.2A9.8 9.8 0 0 1 12 6c6.4 0 10 6 10 6a18 18 0 0 1-2.4 3.2M6.6 6.7A18 18 0 0 0 2 12s3.6 7 10 7a9.6 9.6 0 0 0 4.3-1M2 2l20 20" />
      <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
    </svg>
  )
}
